<template>
<base-layout :page-title="pageTitle">
<div id='login-page' class='login-container'>
  <div class="logo-section ion-padding ion-text-center">
    <h1 class="main-title">{{ t('GiraffeTitle') }}</h1>
    <p class="subtitle">AI for psychological support</p>
  </div>
  
  <div class="form-section ion-padding">
    <div class="oauth-buttons">
      <ion-button expand="block" class="social-btn google-btn" @click="loginWithProvider('google')">
        <ion-icon :icon="logoGoogle" slot="start"></ion-icon>
        Continue with Google
      </ion-button>
    </div>

    <div class="divider">
      <span>OR</span>
    </div>

    <!-- Step 1: Enter email to request a code -->
    <form v-if="!otpSent" @submit.prevent="handleSendOtp" class="magic-link-form">
      <ion-item class="custom-input">
        <ion-label position="floating">{{ t('email') }}</ion-label>
        <ion-input
          v-model="email"
          type="email"
          name="email"
          autocomplete="email"
          required
        ></ion-input>
      </ion-item>
       
      <div class="ion-text-center login-btn-wrapper">
        <ion-button type="submit" class="submit-btn" expand="block" shape="round">
          Send Code
        </ion-button>
      </div>
    </form>

    <!-- Step 2: Enter the 6-digit OTP -->
    <form v-else @submit.prevent="handleVerifyOtp" class="magic-link-form">
      <p class="otp-hint">A code was sent to <strong>{{ email }}</strong>. Enter it below.</p>
      <ion-item class="custom-input">
        <ion-label position="floating">Login Code</ion-label>
        <ion-input
          v-model="otp"
          type="number"
          inputmode="numeric"
          maxlength="8"
          required
        ></ion-input>
      </ion-item>

      <div class="ion-text-center login-btn-wrapper">
        <ion-button type="submit" class="submit-btn" expand="block" shape="round">
          Verify Code
        </ion-button>
        <ion-button fill="clear" class="back-btn" @click="otpSent = false">
          Use a different email
        </ion-button>
      </div>
    </form>
    
  </div>
</div>
</base-layout>
</template>

<script setup>
import { supabase } from '../supabase';
import { api } from '../api';
import {
  useIonRouter,
  IonItem,
  IonLabel,
  IonInput,
  IonButton,
  IonIcon,
  toastController,
  loadingController,
} from '@ionic/vue';
import { logoGoogle } from 'ionicons/icons';
import { ref, onMounted } from 'vue';

// localization
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
const pageTitle = t('login');

const router = useIonRouter();
const email = ref('');
const otp = ref('');
const otpSent = ref(false);

const showToast = async (msg) => {
  const toast = await toastController.create({
    message: msg,
    duration: 3000,
    color: 'dark'
  });
  await toast.present();
};

const loginWithProvider = async (provider) => {
  const loader = await loadingController.create({ message: 'Redirecting...' });
  await loader.present();
  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: provider,
      options: {
        redirectTo: window.location.origin + '/tabs/chat'
      }
    });
    if (error) throw error;
  } catch (error) {
    showToast(error.message || `Failed to login with ${provider}`);
  } finally {
    await loader.dismiss();
  }
};

// Step 1: send a 6-digit OTP to the user's email
const handleSendOtp = async () => {
  if (!email.value) {
    showToast('Please enter your email address');
    return;
  }
  
  const loader = await loadingController.create({ message: 'Sending code...' });
  await loader.present();

  try {
    const { error } = await supabase.auth.signInWithOtp({
      email: email.value,
      options: {
        shouldCreateUser: true,
      }
    });
    
    if (error) throw error;
    otpSent.value = true;
    showToast('Check your email for the 6-digit code!');
  } catch (error) {
    showToast(error.message || 'Failed to send code');
  } finally {
    await loader.dismiss();
  }
};

// Step 2: verify the OTP the user typed in
const handleVerifyOtp = async () => {
  if (!otp.value || otp.value.length < 4) {
    showToast('Please enter the code from your email');
    return;
  }

  const loader = await loadingController.create({ message: 'Verifying...' });
  await loader.present();

  try {
    const { error } = await supabase.auth.verifyOtp({
      email: email.value,
      token: otp.value,
      type: 'email',
    });

    if (error) throw error;
    router.replace('/tabs/chat');
  } catch (error) {
    showToast(error.message || 'Invalid or expired code. Please try again.');
  } finally {
    await loader.dismiss();
  }
};

onMounted(async () => {
  // Check Supabase Auth State
  const { data: { session } } = await supabase.auth.getSession();
  if (session) {
    router.replace('/tabs/chat');
    return;
  }

  // Fallback to existing api check just in case
  const user_in = api.user();
  if (user_in) {
    router.replace('/tabs/chat');
  } 
});

</script>

<style scoped>
.login-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--ion-color-light); /* Assuming Ionic light mode is default, adjust if dark */
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); /* Adds a premium feel */
}

/* For Dark mode support */
@media (prefers-color-scheme: dark) {
  .login-container {
     background: linear-gradient(135deg, #1a1c29 0%, #2a2d3e 100%);
  }
}

.logo-section {
  flex: 0.4;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 4rem;
}

.main-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin: 0;
  background: -webkit-linear-gradient(45deg, var(--ion-color-primary), var(--ion-color-tertiary));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -1px;
}

.subtitle {
  font-size: 1.1rem;
  color: var(--ion-color-medium);
  margin-top: 0.5rem;
}

.form-section {
  flex: 0.6;
  background: var(--ion-background-color);
  border-top-left-radius: 30px;
  border-top-right-radius: 30px;
  padding: 2rem;
  box-shadow: 0 -10px 40px rgba(0,0,0,0.05);
}

.oauth-buttons {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
}

.social-btn {
  --border-radius: 12px;
  font-weight: 600;
  font-size: 1.1rem;
  height: 54px;
}

.google-btn {
  --background: #ffffff;
  --color: #444444;
  --border-style: solid;
  --border-width: 1px;
  --border-color: #dddddd;
  --box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

/* Override Google btn styling for dark mode */
@media (prefers-color-scheme: dark) {
  .google-btn {
    --background: #2b2b2b;
    --color: #ffffff;
    --border-color: #444444;
  }
}

.apple-btn {
  --background: #000000;
  --color: #ffffff;
  --box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 1.5rem 0;
  color: var(--ion-color-medium);
}

.divider::before, .divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid var(--ion-color-light-shade);
}

.divider span {
  padding: 0 10px;
  font-size: 0.9rem;
  font-weight: 500;
}

.custom-input {
  --background: var(--ion-color-light);
  --border-radius: 12px;
  --padding-start: 1rem;
  margin-bottom: 1.5rem;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.02);
}

.submit-btn {
  height: 54px;
  font-size: 1.1rem;
  font-weight: 700;
  --box-shadow: 0 8px 16px rgba(var(--ion-color-primary-rgb), 0.3);
  transition: transform 0.2s ease;
}

.submit-btn:active {
  transform: scale(0.98);
}

.register-link-wrapper {
  margin-top: 2rem;
}

.register-text {
  color: var(--ion-color-medium);
  font-size: 1rem;
}

.register-link {
  color: var(--ion-color-primary);
  font-weight: 600;
  text-decoration: none;
  margin-left: 0.5rem;
}

.otp-hint {
  font-size: 0.95rem;
  color: var(--ion-color-medium);
  text-align: center;
  margin-bottom: 1.25rem;
  line-height: 1.5;
}

.back-btn {
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: var(--ion-color-medium);
}
</style>
